import {
  CalendarTypes,
  type CalendarEvent,
  type StationBrand,
  type StationEvents,
  type CalendarEventSchedule,
} from '@centric-os/types';
import { DateTime, Duration } from 'luxon';
import type FullCalendar from '@fullcalendar/vue3';
import type { Calendar } from '@fullcalendar/core';

import { type Ref, computed } from 'vue';
import { useDateHelper } from '@centric-os/helpers';

const enum CDLCalendarDurationUnits {
  MINUTES = 'minutes',
  HOURS = 'hours',
  DAYS = 'days',
  WEEKS = 'weeks',
}
export default (fullCalendarRef?: Ref<InstanceType<typeof FullCalendar>>) => {
  const customCalendarOptionsForStationEvents = computed(() => {
    return {
      timeZone: 'UTC',
      initialView: 'timeGridWeek',
      weekends: true,
      slotDuration: '1:00:00',
      slotEventOverlap: false,
      expandRows: true,
      height: 'auto',
      headerToolbar: false,
      allDayText: '',
      dayHeaderFormat: { weekday: 'long', day: 'numeric' },
      eventOrderStrict: true,
      eventTimeFormat: {
        hour: 'numeric',
        minute: '2-digit',
        meridiem: true,
      },
      events: [],
      // @ts-expect-error FIXME: Parameter 'info' implicitly has an 'any' type.
      eventContent(info) {
        return {
          html: getEventTemplate(info),
        };
      },
      // @ts-expect-error FIXME: Parameter 'date' implicitly has an 'any' type.
      dayHeaderContent(date) {
        return {
          html: getDayHeaderContent(date),
        };
      },
    };
  });
  const stationColors = [
    '#ABC9FB',
    '#A0E6BA',
    '#F6DE95',
    '#94E0ED',
    '#F7B0D3',
    '#BCBDF9',
    '#9AE0D9',
    '#FCC39B',
    '#DAB6FC',
    '#FFACA7',
  ];
  const disabledStationColors = [
    '#D0E1FD',
    '#CFF3DD',
    '#FBEFCA',
    '#C9EFF6',
    '#FBD7E9',
    '#DDDEFC',
    '#CDEFEC',
    '#FDE1CD',
    '#EDDBFD',
    '#FFD5D3',
  ];

  const getEventDateStart = (schedule: CalendarEventSchedule) => {
    if (!schedule?.start) return 'No start date provided';

    let start = DateTime.fromMillis(schedule?.start);
    start = start.plus({ minutes: start?.offset }).setZone('utc');

    const times = schedule?.times?.[0] || start.toFormat('HH:mm');
    const [hours, minutes] = times.split(':');
    return start
      .startOf('day')
      .plus({ hours: parseInt(hours), minutes: parseInt(minutes) })
      .toISO();
  };

  const getEventDateEnd = (schedule: CalendarEventSchedule) => {
    if (schedule?.end) return DateTime.fromMillis(schedule.end).setZone('utc').endOf('day');
    if (schedule?.year && schedule?.month && schedule?.dayOfMonth) {
      const { year, month, dayOfMonth } = schedule;
      return DateTime.fromObject({
        year: year[0],
        month: month[0] + 1,
        day: dayOfMonth[0],
      }).setZone('utc');
    }
    return undefined;
  };
  const getEventDuration = (schedule: CalendarEventSchedule) => {
    if (schedule?.start && schedule?.end && !schedule?.durationUnit)
      return schedule?.end - schedule?.start;
    const durationUnit = schedule?.durationUnit;
    if (!durationUnit) return 86400000; // One day
    if (durationUnit === CDLCalendarDurationUnits.MINUTES)
      return Duration.fromObject({ minutes: schedule?.duration }).as('milliseconds');
    if (durationUnit === CDLCalendarDurationUnits.HOURS)
      return Duration.fromObject({ hours: schedule?.duration }).as('milliseconds');
    if (durationUnit === CDLCalendarDurationUnits.DAYS)
      return Duration.fromObject({ days: schedule?.duration }).as('milliseconds');
    if (durationUnit === CDLCalendarDurationUnits.WEEKS)
      return Duration.fromObject({ weeks: schedule?.duration }).as('milliseconds');
  };
  const getSingleStationsEvents = (
    locationCalendarEvents: StationEvents = {},
    station: StationBrand,
  ) => {
    const stationEvents = locationCalendarEvents?.[station?.id];
    const menuIds = Object.keys(stationEvents?.menuEvents || {});
    const calendarEvents = menuIds.reduce((acc, id) => {
      if (stationEvents.menuEvents?.[id]?.length) {
        stationEvents.menuEvents[id].forEach((menuEvent) => {
          const newEvent = buildEvent({
            calendarEvent: menuEvent,
            station: station,
          });
          acc.push(newEvent);
        });
      }
      return acc;
    }, []);
    locationCalendarEvents?.[station?.id]?.closureEvents.forEach((closureEvent: CalendarEvent) => {
      const newEvent = buildEvent({
        calendarEvent: closureEvent,
        station: station,
      });
      calendarEvents.push(newEvent);
    });
    return calendarEvents;
  };

  const getMultipleStationsEvents = (
    locationCalendarEvents: StationEvents = {},
    currentStation: StationBrand,
    allStations: StationBrand[],
  ) => {
    // @ts-expect-error FIXME: Variable 'allEvents' implicitly has type 'any[]' in some locations where its ...
    const allEvents = [];
    // @ts-expect-error FIXME: Variable 'allEvents' implicitly has an 'any[]' type.
    if (!locationCalendarEvents) return allEvents;
    for (const [stationId, stationEvents] of Object.entries(locationCalendarEvents)) {
      const station = allStations.find((stationBrand) => stationBrand?.id === stationId);
      if (station?.id === currentStation?.id) {
        locationCalendarEvents?.[station?.id]?.closureEvents.forEach(
          (closureEvent: CalendarEvent) => {
            const newEvent = buildEvent({ calendarEvent: closureEvent, station: station });
            allEvents.push(newEvent);
          },
        );
      }
      for (const [menuId, menuEvents] of Object.entries(stationEvents.menuEvents)) {
        if (menuEvents) {
          menuEvents.forEach((menuEvent) => {
            const newEvent = buildEvent({
              calendarEvent: menuEvent,
              station,
            });

            allEvents.push(newEvent);
          });
        }
      }
    }

    // @ts-expect-error FIXME: Variable 'allEvents' implicitly has an 'any[]' type.
    return allEvents;
  };

  const buildEvent = ({
    calendarEvent,
    station,
  }: {
    calendarEvent: CalendarEvent;
    station?: StationBrand;
  }) => {
    const data = calendarEvent?.data;
    const schedule = calendarEvent?.schedule;
    const byWeekDay = schedule?.dayOfWeek?.map((day) => (day === 0 ? 6 : day - 1)); // FullCal: MON == 0. CDL Cal: SUN == 0
    const isClosure = data.calendar === CalendarTypes.HOLIDAY;
    const disabled = data?.is?.disabled || station?.is?.hidden;
    const color = disabled ? station?.color?.disabledColor : station?.color?.enabledColor;
    const event: { [key: string]: any } = {
      title: data?.title,
      className: isClosure ? 'station-closure' : '',
      color: color,
      display: isClosure ? 'background' : '',
      borderColor: 'white',
      // extended props below
      // @ts-expect-error FIXME: Property 'week' does not exist on type 'boolean | { week: number; }'.
      interval: schedule?.every?.week,
      menuEventDisabled: data?.is?.disabled,
      isPickup: data?.is?.pickup,
      isDelivery: data?.is?.delivery,
      menuId: data?.menu,
      repeats: byWeekDay || [0, 1, 2, 3, 4, 5, 6],
      event: { data, schedule },
      station: station,
    };

    if (isClosure && schedule?.start && schedule?.end) {
      const start = DateTime.fromMillis(calendarEvent.schedule.start);
      const end = DateTime.fromMillis(calendarEvent.schedule.end);
      event.start = start.plus({ minutes: start?.offset }).toISO();
      event.end = end.plus({ minutes: start?.offset }).toISO();
    } else {
      event.duration = getEventDuration(schedule);
      event.rrule = {
        byweekday: isClosure ? undefined : byWeekDay || [0, 1, 2, 3, 4, 5, 6],
        // @ts-expect-error FIXME: Property 'week' does not exist on type 'boolean | { week: number; }'.
        interval: schedule?.every?.week || 1,
        freq: 'weekly',
        dtstart: getEventDateStart(schedule),
        until: getEventDateEnd(schedule),
      };
    }

    return event;
  };
  const getStationColor = (isActiveStation: boolean, station: StationBrand, colorIndex: number) => {
    if (isActiveStation)
      return { enabledColor: stationColors[0], disabledColor: disabledStationColors[0] };
    return {
      enabledColor: stationColors.slice(1)?.[colorIndex],
      disabledColor: disabledStationColors.slice(1)?.[colorIndex],
    };
  };
  // @ts-expect-error FIXME: Parameter 'date' implicitly has an 'any' type.
  const getDayHeaderContent = (date) => {
    const [number, weekday] = date.text.split(' ');
    const api = fullCalendarRef?.value.getApi() as Calendar;
    if (api?.view?.type === 'timeGridWeek')
      return `<span class="font-semibold">${number}</span><br/><span class="font-light">${weekday}</span>`;
    return `<div class='flex items-center py-4 px-1'> <span class="font-semibold mr-1">${number}</span>  <span class="font-light">${weekday}</span></div>`;
  };
  // @ts-expect-error FIXME: Parameter 'info' implicitly has an 'any' type.
  const getEventTemplate = (info) => {
    const backgroundEvent = info?.event?.display === 'background';
    const interval = info?.event?.extendedProps?.interval;
    const disabled =
      info.event?.extendedProps?.menuEventDisabled ||
      info.event?.extendedProps?.station?.is?.hidden;
    const start = DateTime.fromISO(info.event.startStr).setZone('utc');
    const end = DateTime.fromISO(info.event.endStr).setZone('utc');
    const startString = toLowerCaseAMPM(start.toFormat('hh:mm a'));
    const endString = toLowerCaseAMPM(end.toFormat('hh:mm a'));
    let allDay = false;
    if (backgroundEvent) {
      allDay = Boolean(
        start.hour === 0 && start.minute === 0 && end.hour === 0 && end.minute === 0,
      );
    }
    return `
        <div class='flex flex-col test overflow-hidden whitespace-nowrap h-full p-1 
        ${backgroundEvent ? 'station-closure' : 'cursor-pointer'}
         ${disabled ? 'event-card-inactive' : 'event-card-active'}'
         > 
          <div class="flex items-center"> 
            ${disabled ? '<i class="material-symbols-rounded mr-1" >visibility_off</i>' : ''}
            <p class="m-0 font-semibold text-[0.875rem] whitespace-normal">
              ${info.event.title} 
            </p>   
          </div>
          ${backgroundEvent ? "<div class='mt-2 whitespace-normal'> Station closure - </div>" : ''}

          <p class='m-0 text-[0.875rem]  whitespace-normal'>${
            allDay ? 'All day' : `${startString} - ${endString}`
          }
          </p> 
        ${
          interval > 1
            ? `<div class='mt-auto flex items-center'> 
                  <i class="material-symbols-rounded text-[var(--gray-800)] mr-1">sync</i> 
                  <span class='font-light text-[var(--gray-800)]'>${interval} weeks </span> 
                </div>`
            : ''
        }
        </div>`;
  };

  function getEventTimesDuration(event: CalendarEvent) {
    const duration = getEventDuration(event?.schedule);
    const startTime = DateTime.fromISO(getEventDateStart(event?.schedule)).setZone('utc');
    const endTime = startTime.plus({ millisecond: duration });
    return `${startTime.toFormat('h:mm a').toLowerCase()} - ${endTime
      .toFormat('h:mm a')
      .toLowerCase()}`;
  }

  function getEventDates(menuEvent: CalendarEvent) {
    const startTime = DateTime.fromISO(getEventDateStart(menuEvent?.schedule)).setZone('utc');
    const endTime = getEventDateEnd(menuEvent?.schedule);
    return `${startTime.toFormat('MMMM d, yyyy')} - ${
      endTime?.toFormat('MMMM d, yyyy') || 'No end date'
    }`;
  }
  /**
   * Converts AM/PM to lowercase and adds a space between the time and AM/PM if there is none
   * @param timeDate a string containing a time and date
   * @returns returns the time and date with AM/PM in lowercase and if there is no space between the time and AM/PM, it adds a space (eg. 'January 1, 2022 1:00PM to January 1, 2022 1:00 pm ')
   */
  function toLowerCaseAMPM(timeDate: string): string {
    return timeDate.replace(/(\d)(AM|PM)|\bAM\b|\bPM\b/g, (match, digit, meridiem) => {
      if (digit && meridiem) {
        return `${digit} ${meridiem.toLowerCase()}`;
      }
      return match.toLowerCase();
    });
  }

  /**
   * Get the date + time range for an event as a string
   * @param event the event to get the date + time range for
   * @returns the range of date + time for the event (eg. 'January 1, 2022 1:00 pm - January 2, 2022 2:00 pm')
   */
  function getEventDateTimes(event: CalendarEvent): string {
    const { getDateFromTimestamp } = useDateHelper();
    let startDateTime = DateTime.fromJSDate(getDateFromTimestamp(event.schedule.start))
      .toFormat('MMMM d, yyyy h:mma')
      .replace(/(\d)(AM|PM)/g, '$1 $2');
    startDateTime = toLowerCaseAMPM(startDateTime);

    let endDateTime = event.schedule.end
      ? DateTime.fromJSDate(getDateFromTimestamp(event.schedule.end))
          .toFormat('MMMM d, yyyy h:mma')
          .replace(/(\d)(AM|PM)/g, '$1 $2')
      : 'No end date';
    endDateTime = toLowerCaseAMPM(endDateTime);

    return `${startDateTime} - ${endDateTime}`;
  }

  return {
    getEventDateStart,
    getEventDateEnd,
    getEventDuration,
    stationColors,
    getSingleStationsEvents,
    getMultipleStationsEvents,
    getEventTemplate,
    getDayHeaderContent,
    getEventTimesDuration,
    getEventDates,
    getEventDateTimes,
    getStationColor,
    customCalendarOptionsForStationEvents,
    toLowerCaseAMPM,
  };
};
